import * as React from "react";
import { Form, Checkbox, useFormState, useFormApi, Option } from "informed";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useParams, Link } from "react-router-dom";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { ChallengeDecisionMaker, StudyPefSetting } from "../../../misc/api/apiTypes";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";
import { Dropdown } from "../../../forms/informedv2/Dropdown";

interface PefSettingsScreenFormState {
    maximumTests: number;
    selectedTestParameters: ISelectListItem[];
    selectedAnalysisParameters: ISelectListItem[];
    selectedRepeatabilityParameters: ISelectListItem[];
    testAcceptanceChange: boolean;
    testAcceptanceChangeWindow: number;
    decisionMaker: ChallengeDecisionMaker;
    overreadEnabled: boolean;
}

interface PefSettingsScreenFormProps {
    studyId: string;
    parameters: any[];
}

export const PefSettingsScreenForm = ({ studyId, parameters }: PefSettingsScreenFormProps) => {
    const formApi = useFormApi<PefSettingsScreenFormState>();
    const formState = useFormState<PefSettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const convertToIdsArray = (params: ISelectListItem[]) => {
        return params.length > 0
            ? parameters.filter(p => params.map(s => s.Value).includes(p.Keyword)).map(p => p.Id)
            : null;
    }

    const onPreAuditSubmit = () => {
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const pefSettings: StudyPefSetting = {
            SelectedTestParameters: convertToIdsArray(state.values.selectedTestParameters),
            SelectedAnalysisParameters: convertToIdsArray(state.values.selectedAnalysisParameters),
            SelectedRepeatabilityParameters: convertToIdsArray(state.values.selectedRepeatabilityParameters),
            MaxTests: state.values.maximumTests,
            TestAcceptanceChange: state.values.testAcceptanceChange,
            TestAcceptanceChangeTimeWindow: state.values.testAcceptanceChangeWindow,
            IsOverreadingAvailable: state.values.overreadEnabled,
            SelectedFinalTestAcceptabilityDecisionMaker: state.values.decisionMaker,
        }

        try {
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Pef.StudyPefSetting", pefSettings);

            if (result) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    }

    const maximumTestValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 4, 8);
    }

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false,
            Category: p.Category
        };
    });

    const repeatabilityParameterOptions = parametersSelectList.filter(p => p.Category === 'RepeatabilityParameter');
    const analysisParameterOptions = parametersSelectList.filter(p => p.Category === 'AnalysisParameter');
    const testParameterOptions = parametersSelectList.filter(p => p.Category !== 'RepeatabilityParameter');

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 data-automationid="PefSettingsID" className="text-primary">PEF Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Maximum Tests</label>
                                    <p className="help-block">Configures the maximum number of tests allowed</p>
                                    <TextBox<number, PefSettingsScreenFormState> data-automationid="MaximumTestsTextBox" id="maximumTests" field="maximumTests" type="number" min="4" max="8" required validate={maximumTestValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Displayed Test Parameters</label>
                                    <p className="help-block">The test parameters to be displayed on subject testing screens for supported applications</p>
                                    <MultiSelect
                                        id="selectedTestParameters"
                                        field="selectedTestParameters"
                                        options={testParameterOptions}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Test Acceptance Change Allowed</label>
                                    <p className="help-block">Configures whether test accceptance changes are allowed and the window of time after session completion</p>
                                    <Checkbox<boolean, PefSettingsScreenFormState> data-automationid="TestAcceptanceChangeAllowedCheckbox" field="testAcceptanceChange" />
                                    <TextBoxWithStaticSuffix<number, PefSettingsScreenFormState> data-automationid="TestAcceptabilityChangeTimeWindowTextBox" field="testAcceptanceChangeWindow" type="number" min="0" max="120" staticpart="Hours" disabled={!formState.values.testAcceptanceChange} validate={(value) => IntegerValidator(value) || MinMaxValidator(value, 0, 120)} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Overreading</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Over-Read enabled</label>
                                    <Checkbox data-automationid="OverreadEnabledCheckbox" id="overreadEnabledToggle" className="checkbox" field="overreadEnabled" />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Final Test Acceptability Decision Maker</label>
                                    <p className="help-block">Configures whether the final test acceptability is make by the Over-Reader or the Technician</p>
                                    <Dropdown id="decisionMaker" data-automationid="FinalTestAcceptabilityDecisionMakerDropdown" field="decisionMaker" disabled={!formState.values.overreadEnabled}>
                                        <Option data-automationid="DecisionMakerOverreader">Over-reader</Option>
                                        <Option data-automationid="DecisionMakerTechnician">Technician</Option>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Analysis Parameters</label>
                                    <p className="help-block">The test parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="AnalysisParametersDropdown"
                                        field="selectedAnalysisParameters"
                                        options={analysisParameterOptions}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Repeatability Parameters</label>
                                    <p className="help-block">The repeatability parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="RepeatabilityParametersDropdown"
                                        field="selectedRepeatabilityParameters"
                                        options={repeatabilityParameterOptions}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
                {auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={(audit) => onSubmit(audit)}
                    />
                ) : null}
            </div>
        </>
    );
}

export const PefSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<PefSettingsScreenFormState>();
    const [parameters, setParameters] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {
            const pefParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.Pef);
            setParameters(pefParametersResult);

            const pefSettings = await legacyClient.getStudyPefSettings(id);

            setInitialValues({
                maximumTests: pefSettings.MaxTests,
                testAcceptanceChange: pefSettings.TestAcceptanceChange,
                testAcceptanceChangeWindow: pefSettings.TestAcceptanceChangeTimeWindow,
                selectedTestParameters: ConvertToSelectListItemArray(pefSettings.SelectedTestParameters, pefParametersResult),
                selectedAnalysisParameters: ConvertToSelectListItemArray(pefSettings.SelectedAnalysisParameters, pefParametersResult),
                selectedRepeatabilityParameters: ConvertToSelectListItemArray(pefSettings.SelectedRepeatabilityParameters, pefParametersResult),
                overreadEnabled: pefSettings.IsOverreadingAvailable,
                decisionMaker: pefSettings.SelectedFinalTestAcceptabilityDecisionMaker,
            });
        }

        getSettings();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading PEF Settings..." />;
    }

    const validateFields = (values) => {
        return {
            selectedTestParameters: values.selectedTestParameters === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form noValidate initialValues={initialValues} validateFields={validateFields}>
            <PefSettingsScreenForm studyId={id} parameters={parameters} />
        </Form>
    );
}
